import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, Upload, FileText, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SERVICES } from "../data/site";

const inputCls =
  "text-lg h-13 min-h-[52px] border-2 border-gray-400 focus:border-brand-darkBlue rounded-xl px-4";
const labelCls = "text-base font-semibold text-brand-darkBlue mb-2 block";

const SubmitButton = ({ loading, children, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-red text-white font-semibold py-4 px-10 rounded-full hover:bg-brand-redDark transition-colors focus:outline-none focus:ring-4 focus:ring-red-200 disabled:opacity-70"
  >
    {loading && <Loader2 className="w-5 h-5 animate-spin" />}
    {children}
  </button>
);

export const ConsultationForm = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferred_date: "",
    message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/consultation", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", service: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="consultation-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="c-name">Full Name</Label>
          <Input id="c-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="consultation-name" placeholder="Jane Doe" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="c-phone">Phone Number</Label>
          <Input id="c-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="consultation-phone" placeholder="(123) 456-7890" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="c-email">Email Address</Label>
          <Input id="c-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="consultation-email" placeholder="you@email.com" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="c-date">Preferred Date</Label>
          <Input id="c-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="consultation-date" />
        </div>
      </div>
      <div>
        <Label className={labelCls}>Service of Interest</Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger className={inputCls} data-testid="consultation-service">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.slug} value={s.title} className="text-base">{s.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className={labelCls} htmlFor="c-msg">How can we help?</Label>
        <Textarea id="c-msg" value={form.message} onChange={set("message")} rows={4} className="text-lg border-2 border-gray-400 focus:border-brand-darkBlue rounded-xl px-4 py-3" data-testid="consultation-message" placeholder="Tell us about your care needs..." />
      </div>
      <SubmitButton loading={loading} testid="consultation-submit">Request Consultation</SubmitButton>
    </form>
  );
};

export const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="ct-name">Full Name</Label>
          <Input id="ct-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" placeholder="Jane Doe" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="ct-email">Email Address</Label>
          <Input id="ct-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" placeholder="you@email.com" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="ct-phone">Phone Number</Label>
          <Input id="ct-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" placeholder="(123) 456-7890" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="ct-subject">Subject</Label>
          <Input id="ct-subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" placeholder="How can we help?" />
        </div>
      </div>
      <div>
        <Label className={labelCls} htmlFor="ct-msg">Message</Label>
        <Textarea id="ct-msg" required value={form.message} onChange={set("message")} rows={5} className="text-lg border-2 border-gray-400 focus:border-brand-darkBlue rounded-xl px-4 py-3" data-testid="contact-message" placeholder="Write your message..." />
      </div>
      <SubmitButton loading={loading} testid="contact-submit">Send Message</SubmitButton>
    </form>
  );
};

export const ApplicationForm = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resume, setResume] = useState(null); // {resume_path, resume_filename}
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResume({ resume_path: data.resume_path, resume_filename: data.resume_filename });
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload resume.");
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setUploading(false);
    }
  };

  const removeResume = () => {
    setResume(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/application", { ...form, ...(resume || {}) });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
      removeResume();
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="application-form">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="a-name">Full Name</Label>
          <Input id="a-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="application-name" placeholder="Jane Doe" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="a-phone">Phone Number</Label>
          <Input id="a-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="application-phone" placeholder="(123) 456-7890" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className={labelCls} htmlFor="a-email">Email Address</Label>
          <Input id="a-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="application-email" placeholder="you@email.com" />
        </div>
        <div>
          <Label className={labelCls} htmlFor="a-position">Position Applying For</Label>
          <Input id="a-position" required value={form.position} onChange={set("position")} className={inputCls} data-testid="application-position" placeholder="e.g. Registered Nurse, Home Health Aide" />
        </div>
      </div>
      <div>
        <Label className={labelCls} htmlFor="a-exp">Relevant Experience</Label>
        <Input id="a-exp" value={form.experience} onChange={set("experience")} className={inputCls} data-testid="application-experience" placeholder="Years of experience / certifications" />
      </div>
      <div>
        <Label className={labelCls}>Resume / CV (PDF, DOC, DOCX — max 10MB)</Label>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={onFile}
          className="hidden"
          data-testid="application-resume-input"
        />
        {!resume ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="application-resume-upload"
            className="w-full flex items-center justify-center gap-3 border-2 border-dashed border-gray-400 rounded-xl py-5 px-4 text-brand-darkBlue font-medium hover:border-brand-darkBlue hover:bg-brand-lightBlue/50 transition-colors disabled:opacity-70"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            {uploading ? "Uploading..." : "Click to upload your resume"}
          </button>
        ) : (
          <div className="flex items-center justify-between gap-3 border-2 border-brand-darkBlue/30 bg-brand-lightBlue rounded-xl py-4 px-4" data-testid="application-resume-selected">
            <span className="flex items-center gap-3 text-brand-darkBlue font-medium truncate">
              <FileText className="w-5 h-5 shrink-0 text-brand-red" />
              <span className="truncate">{resume.resume_filename}</span>
            </span>
            <button type="button" onClick={removeResume} data-testid="application-resume-remove" className="text-gray-500 hover:text-brand-red shrink-0" aria-label="Remove resume">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      <div>
        <Label className={labelCls} htmlFor="a-msg">Tell us about yourself</Label>
        <Textarea id="a-msg" value={form.message} onChange={set("message")} rows={4} className="text-lg border-2 border-gray-400 focus:border-brand-darkBlue rounded-xl px-4 py-3" data-testid="application-message" placeholder="Why would you like to join the HHS team?" />
      </div>
      <SubmitButton loading={loading} testid="application-submit">Apply Here</SubmitButton>
    </form>
  );
};
